import { useMutation, useQuery } from '@apollo/client';
import React, { useMemo, useState } from 'react';
import * as styles from './PublicContentSettings.module.scss';
import Button from '../../components/Button';
import Form, { Field, Submit } from '../../components/Form';
import { gql } from '../../graphql/generated/gql';
import PricingModalButton from '../../organization/PricingModalButton';
import SettingCard from '../../settings/SettingCard';
import getFeature from '../../utils/features';
import nonEmpty from '../../utils/nonEmpty';

const OPTIONS = {
  ALLOW_ALL: 'Allow all iframe hosts',
  ALLOW_SPECIFIC: 'Allow specific iframe hosts',
  DISABLED: 'Disable unintegrated iframe hosts',
} as const;

const ORGANIZATION_ALLOWED_EMBED_HOSTS_QUERY = gql(/** @complexity 3 */ `
  query organizationAllowedEmbedHosts {
    session: currentSession {
      organization {
        id
        settings {
          allowedEmbedHosts
        }
      }
    }
  }
`);

const UPDATE_ALLOWED_EMBED_HOSTS_MUTATION = gql(/** @complexity 4 */ `
  mutation updateAllowedEmbedHosts($allowedEmbedHosts: [String!]!) {
    organization: updateOrganization(
      settings: { allowedEmbedHosts: $allowedEmbedHosts }
    ) {
      id
      settings {
        allowedEmbedHosts
      }
    }
  }
`);

type Values = {
  allowed: keyof typeof OPTIONS;
  hosts: string;
};

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const parseHosts = (hostsString: string | null): string[] =>
  hostsString
    ?.split(',')
    .map((host) => {
      try {
        const trimmed = host.trim();
        return new URL(
          /^https?:\/\//.test(trimmed) ? trimmed : `http://${trimmed}`,
        ).host;
      } catch (e) {
        return '';
      }
    })
    .filter(Boolean) ?? [];

const joinHosts = (hosts: Array<string | null | undefined> | null): string =>
  hosts?.filter(nonEmpty).join(', ') ?? '';

const AllowedIframeHostsSettings = () => {
  const [isEditing, setIsEditing] = useState<boolean>(false);

  const { data } = useQuery(ORGANIZATION_ALLOWED_EMBED_HOSTS_QUERY);

  const [updateEmbedHosts] = useMutation(UPDATE_ALLOWED_EMBED_HOSTS_MUTATION);

  const handleSubmit = async (values: Values) => {
    let allowedEmbedHosts: string[] | null = null;

    if (values.allowed === 'ALLOW_ALL') {
      allowedEmbedHosts = ['*'];
    } else if (values.allowed === 'DISABLED') {
      allowedEmbedHosts = [];
    } else {
      allowedEmbedHosts = parseHosts(values.hosts);
    }

    await updateEmbedHosts({ variables: { allowedEmbedHosts } });
    window.__slab.PUBLIC_ORG.settings.allowedEmbedHosts = allowedEmbedHosts;
    setIsEditing(false);
  };

  const isEditorIFrameEnabled = getFeature('editor_iframe');

  const initialValues: Values = useMemo<Values>(() => {
    const allowedEmbedHosts =
      data?.session?.organization?.settings?.allowedEmbedHosts;
    if (
      !allowedEmbedHosts ||
      allowedEmbedHosts.length === 0 ||
      !isEditorIFrameEnabled
    ) {
      return { allowed: 'DISABLED', hosts: '' };
    }
    if (allowedEmbedHosts.length === 1 && allowedEmbedHosts[0] === '*') {
      return { allowed: 'ALLOW_ALL', hosts: '' };
    }
    return { allowed: 'ALLOW_SPECIFIC', hosts: joinHosts(allowedEmbedHosts) };
  }, [data, isEditorIFrameEnabled]);

  return (
    <>
      {isEditing ? (
        <SettingCard
          title="Allowed iframe hosts"
          description={
            <Form<Values>
              initialValues={initialValues}
              onSubmit={handleSubmit}
              validateOnChange
              validate={(values) =>
                values.allowed === 'ALLOW_SPECIFIC' &&
                parseHosts(values.hosts).length === 0
                  ? { hosts: 'Please include at least one host' }
                  : {}
              }
            >
              {({ values }) => (
                <>
                  <div className={styles.formFields}>
                    <Field
                      label={OPTIONS.ALLOW_ALL}
                      name="allowed"
                      type="radio"
                      value="ALLOW_ALL"
                    />
                    <Field
                      label={OPTIONS.ALLOW_SPECIFIC}
                      name="allowed"
                      type="radio"
                      value="ALLOW_SPECIFIC"
                    />
                    {values.allowed === 'ALLOW_SPECIFIC' && (
                      <Field
                        required
                        type="text"
                        name="hosts"
                        placeholder="Enter hosts separated by commas (e.g. share.getcloudapp.com, loom.com)"
                      />
                    )}
                    <Field
                      label={OPTIONS.DISABLED}
                      name="allowed"
                      type="radio"
                      value="DISABLED"
                    />
                  </div>
                  <div className={styles.formFooter}>
                    <Submit variant="primary">Save</Submit>
                  </div>
                </>
              )}
            </Form>
          }
        />
      ) : (
        <SettingCard
          actions={
            isEditorIFrameEnabled ? (
              <Button onClick={() => setIsEditing(true)} variant="secondary">
                Edit
              </Button>
            ) : (
              <PricingModalButton feature="customEmbedHosts">
                Upgrade
              </PricingModalButton>
            )
          }
          title="Allowed iframe hosts"
          description={OPTIONS[initialValues.allowed]}
        />
      )}
    </>
  );
};

export default AllowedIframeHostsSettings;
